import {
  ActionIcon,
  Flex,
  Loader,
  ScrollArea,
  Table,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { closeModal } from "@mantine/modals";
import { memo, useMemo, useState } from "react";
import { IoIosArrowRoundBack } from "react-icons/io";
import {
  CustomReportInput,
  TransactionStatus,
  useGetPosReportQuery,
} from "src/generated/graphql";
import { ModalId, formatter } from "src/utils/constants";
import { ISOformatedDateTime } from "src/utils/dateHelper";

const header = [
  "Transaction Date and Time",
  "Receipt #",
  "Account Name",
  "Subtotal",
  "Taxes",
  "Total",
  "Payment Method",
  "Employee",
];

type ITableReport = {
  values: CustomReportInput;
};

function TableReport({ values }: ITableReport) {
  const theme = useMantineTheme();
  let color = theme.colors.secondaryTextColor[6];
  const { data, loading } = useGetPosReportQuery({
    variables: { data: values },
  });
  const [cummulativeTotal, setCummulativeTotal] = useState({
    total: 0,
    subTotal: 0,
    tax: 0,
  });

  const rows = useMemo(
    () =>
      data?.posReport.map((e) => {
        if (
          e.transactionStatus === TransactionStatus.Pending ||
          e.transactionStatus === TransactionStatus.Declined
        )
          return;
        if (!e.isRefund && e.transactionStatus === TransactionStatus.Accepted)
          setCummulativeTotal((cummulativeTotal) => ({
            ...cummulativeTotal,
            total: cummulativeTotal.total + e?.amount || 0,
            subTotal: cummulativeTotal.subTotal + e?.subtotal || 0,
            tax: cummulativeTotal.tax + e?.tax || 0,
          }));
        return (
          <tr key={e.id}>
            <td>
              {e.completedAt ? ISOformatedDateTime(e.completedAt) : "N/A"}
            </td>
            <td>{e.id}</td>
            <td>
              {e.chargedTo
                ? `${e.chargedTo?.user.firstName} ${e.chargedTo?.user.lastName}`
                : "N/A"}
            </td>
            <td>{e.subtotal}</td>
            <td>{e.tax}</td>
            <td
              style={{
                color:
                  e.transactionStatus === TransactionStatus.Refunded
                    ? "red"
                    : "green",
              }}
            >
              <span style={{ display: "inline-block", width: 10 }}>
                {e.transactionStatus === TransactionStatus.Refunded && "-"}{" "}
              </span>
              <span>{e.amount}</span>
            </td>
            <td>{e.paymentMethod?.type || "N/A"}</td>
            <td>
              {e.performedBy
                ? `${e.performedBy?.user.firstName} ${e.performedBy?.user.lastName}`
                : "N/A"}
            </td>
          </tr>
        );
      }),
    [data?.posReport]
  );

  const handleBack = () => {
    closeModal(ModalId.ReportView);
  };

  const headerMemo = useMemo(() => {
    return header.map((e) => <th>{e}</th>);
  }, []);

  return (
    <Flex direction={"column"} px={"sm"}>
      <ActionIcon
        mt={"md"}
        onClick={handleBack}
        size={"lg"}
        sx={{
          "&:hover": {
            backgroundColor: theme.colors.secondary[6],
          },
        }}
      >
        <IoIosArrowRoundBack color={color} size={32} />
      </ActionIcon>

      {!rows?.length ? (
        <Flex justify={"center"} align={"center"} mt={"lg"}>
          {loading ? (
            <Loader />
          ) : (
            <Text fw={"bold"} size={"xl"}>
              No Record Found
            </Text>
          )}
        </Flex>
      ) : (
        <>
          <ScrollArea
            style={{ height: "75vh" }}
            type="auto"
            px={"sm"}
            offsetScrollbars
          >
            <Table>
              <thead>
                <tr>{headerMemo}</tr>
              </thead>

              <tbody>{rows}</tbody>
            </Table>
          </ScrollArea>
          <Flex justify={"space-between"}>
            <Flex>
              <Text> Total: </Text>
              <Text ml={5}> {formatter.format(cummulativeTotal.total)} </Text>
            </Flex>
            <Flex>
              <Text> SubTotal:</Text>
              <Text ml={5}> {formatter.format(cummulativeTotal.subTotal)}</Text>
            </Flex>
            <Flex>
              <Text>Taxes:</Text>
              <Text ml={5}>{formatter.format(cummulativeTotal.tax)}</Text>
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
}

export default memo(TableReport);
